// Where the client goes to clear an observed item (PRD 63 §4.4, §5.2).
//
// THE SECTION IS NOT THE ANSWER. Until now a `categorise_transaction` item
// linked to /business/banking — a list of bank accounts — for a question about
// one row of one of them, and `complete_w9` linked to the vendor list. §4.4's
// claim is that these kinds are the ones Karbon, TaxDome and Canopy cannot do
// at all, because they can ask and then must be told, where we can look. Half
// of that is the observation; the other half is putting the client in front of
// the thing, and we were answering it with a lobby.
//
// THE ROUTE IS BUILT HERE, not on the server. The API says what the subject is
// and which books it is in; this file knows the router. A path in the API
// payload would be a second copy of this one, and the copy would be the stale
// one.
//
// NO SUBJECT MEANS NO LINK. The server omits it when the row is gone or when
// the person has no seat on those books — the PFA-only client, and the office
// manager with a seat on one business of three — so an invented fallback link
// would dead-end exactly the people it was invented for.

import { ClientRequestItem, ClientRequestSubject } from '../types/clientRequestApi';

export interface ObservedGuidance {
  /** What actually clears the item, in the client's words. */
  does: string;
  /** Present only when we can put them in front of the thing. */
  to?: string;
  cta?: string;
}

/**
 * The business a deep link lands in, carried in the URL rather than selected
 * before navigating. `/requests` renders ABOVE BusinessProvider — it is a
 * sibling of the /business subtree, not a child — so there is no
 * `selectBusiness` to call from here; BusinessProvider reads this on mount and
 * prefers it over the persisted selection. It also makes the link shareable,
 * which the persisted-selection version could never be.
 */
export const BUSINESS_PARAM = 'business';

function registerPath(subject: Extract<ClientRequestSubject, { type: 'bank_transaction' }>): string {
  const params = new URLSearchParams({
    [BUSINESS_PARAM]: subject.business_id,
    // The whole flagged set, not just this row: a close is blocked on thirty
    // transactions far more often than on one (§4.4), and a client who has to
    // walk back through the request for each is doing our filing for us.
    needs_client: 'true',
    // …and this row on top, with its drawer open, because it is the one they
    // clicked.
    focus: subject.id,
  });
  return `/business/banking/${subject.bank_account_id}/register?${params.toString()}`;
}

export function observedGuidance(item: ClientRequestItem): ObservedGuidance | null {
  switch (item.kind) {
    case 'categorise_transaction': {
      const base = { does: 'This closes by itself once the transaction is categorised in your books.' };
      if (item.subject?.type !== 'bank_transaction') return base;
      return { ...base, to: registerPath(item.subject), cta: 'Open this transaction' };
    }
    case 'connect_account': {
      const named =
        item.subject?.type === 'plaid_item' && item.subject.institution_name
          ? `This closes by itself once ${item.subject.institution_name} is connected again.`
          : 'This closes by itself once the account is connected.';
      if (item.subject?.type !== 'plaid_item') return { does: named };
      return {
        does: named,
        // The accounts list is where a connection is repaired, and it is the
        // whole screen for it — there is no per-connection route to land on.
        to: `/business/banking?${BUSINESS_PARAM}=${item.subject.business_id}`,
        cta: 'Fix this connection',
      };
    }
    case 'complete_w9': {
      const base = { does: 'This closes by itself once the W-9 is on file.' };
      if (item.subject?.type !== 'vendor') return base;
      // There is no vendor DETAIL route — PRD 35's pages are specced, not
      // built — so this opens the vendors list with that vendor's W-9 drawer
      // up, which is the control that actually clears the item.
      const params = new URLSearchParams({
        [BUSINESS_PARAM]: item.subject.business_id,
        w9: item.subject.id,
      });
      return {
        ...base,
        to: `/business/vendors?${params.toString()}`,
        cta: item.subject.name ? `Send ${item.subject.name} a W-9 request` : 'Open this vendor',
      };
    }
    default:
      return null;
  }
}
